import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { cn } from "@/lib/utils";

function sameUserId(a, b) {
  const left = a == null ? "" : String(a).trim();
  const right = b == null ? "" : String(b).trim();
  return Boolean(left) && Boolean(right) && left === right;
}

function userHasActivePurchase(listing, userId) {
  if (!userId || !listing?.sale?.buyer_id) return false;
  return sameUserId(userId, listing.sale.buyer_id);
}

function InterestCheckBox({ checked, className }) {
  return (
    <span
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 text-[13px] font-black leading-none transition-colors",
        checked
          ? "border-matpro-green bg-matpro-green text-white"
          : "border-matpro-green/60 bg-white text-transparent",
        className,
      )}
      aria-hidden
    >
      ✓
    </span>
  );
}

/**
 * Case « Je suis intéressé » — fiche annonce ou carte listing.
 * Notifie le vendeur / fournisseur (Option A : waitlist étendue).
 */
export function ListingInterestToggle({
  listing,
  mode = "sale",
  onUpdated,
  className = "",
  variant = "default",
  showWhenReserved = false,
}) {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const sellerId = listing?.seller_id || listing?.seller?.id;
  const isOwner = user && sameUserId(user.id, sellerId);
  const hasPurchase = userHasActivePurchase(listing, user?.id);
  const status = listing?.status || "published";
  const isSold = status === "sold";
  const isReserved = status === "reserved";
  const isAvailable =
    status === "published" ||
    status === "pending" ||
    (showWhenReserved && isReserved);

  const deriveChecked = () => Boolean(listing?.on_waitlist || hasPurchase);
  const [checked, setChecked] = useState(deriveChecked);

  useEffect(() => {
    setChecked(deriveChecked());
  }, [listing?.id, listing?.on_waitlist, listing?.sale?.buyer_id, listing?.sale?.id, hasPurchase]);

  if (!listing?.id || isOwner || isSold || !isAvailable) return null;

  const isCard = variant === "card";
  const locked = hasPurchase;
  const label = isReserved
    ? t("interest.waitlist_label")
    : mode === "rental"
      ? t("interest.rental_label")
      : t("interest.sale_label");
  const shortLabel = mode === "rental" ? t("interest.card_rental") : t("interest.card_sale");
  const hint = locked
    ? t("interest.purchase_active_hint")
    : isReserved
      ? t("interest.waitlist_hint")
      : mode === "rental"
        ? t("interest.rental_hint")
        : t("interest.sale_hint");

  const toggle = async () => {
    if (busy || locked) return;
    const next = !checked;

    if (!user) {
      toast.info(t("interest.login_required"));
      navigate("/connexion");
      return;
    }

    const prev = checked;
    setChecked(next);
    setBusy(true);
    try {
      if (next) {
        const { data } = await api.post(`/listings/${listing.id}/waitlist`, { listing_mode: mode });
        setChecked(true);
        if (data.already_exists) {
          toast.info(t("interest.already"));
        } else {
          toast.success(
            isReserved
              ? t("interest.toast_waitlist")
              : mode === "rental"
                ? t("interest.toast_rental")
                : t("interest.toast_sale"),
          );
        }
        onUpdated?.({ ...data, on_waitlist: true });
      } else {
        const { data } = await api.delete(`/listings/${listing.id}/waitlist`);
        setChecked(false);
        toast.success(t("interest.removed"));
        onUpdated?.({ ...data, on_waitlist: false });
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      if (/réservée pour vous|reserved for you|voor u gereserveerd/i.test(msg)) {
        setChecked(true);
        onUpdated?.({ on_waitlist: true });
        toast.info(t("interest.purchase_active_hint"));
      } else {
        setChecked(prev);
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleActivate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    }
  };

  const boxClass = cn(
    checked
      ? "border-matpro-green bg-matpro-green-light/50 shadow-sm"
      : "border-matpro-green/40 bg-white hover:border-matpro-green hover:bg-matpro-green-light/20",
    locked && "cursor-default",
    busy && "pointer-events-none opacity-70",
    className,
  );

  if (isCard) {
    return (
      <div
        role="checkbox"
        aria-checked={checked}
        aria-disabled={locked || busy}
        tabIndex={locked ? -1 : 0}
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex items-center gap-3 rounded-md border-2 px-3 py-2.5 transition-all outline-none focus-visible:ring-2 focus-visible:ring-matpro-green/40",
          locked ? "cursor-default" : "cursor-pointer",
          boxClass,
        )}
        data-testid={`listing-interest-${mode}`}
      >
        <InterestCheckBox checked={checked} data-testid={`listing-interest-check-${mode}`} />
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <Bell className={cn("h-4 w-4 shrink-0", checked ? "text-matpro-green" : "text-matpro-anthracite-muted")} />
          <span className="text-sm font-black text-matpro-anthracite">{shortLabel}</span>
        </span>
      </div>
    );
  }

  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-disabled={locked || busy}
      tabIndex={locked ? -1 : 0}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      className={cn(
        "flex items-start gap-3 rounded-md border px-4 py-3 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-matpro-green/40",
        locked ? "cursor-default" : "cursor-pointer",
        checked
          ? "border-matpro-green/50 bg-matpro-green-light/30"
          : "border-matpro-border bg-matpro-bg/50 hover:border-matpro-green/40",
        busy && "opacity-70 pointer-events-none",
        className,
      )}
      data-testid={`listing-interest-${mode}`}
    >
      <InterestCheckBox checked={checked} className="mt-0.5" data-testid={`listing-interest-check-${mode}`} />
      <span className="min-w-0 flex-1 select-none">
        <span className="block text-sm font-bold text-matpro-anthracite">{label}</span>
        <span className="mt-0.5 block text-xs text-matpro-anthracite-muted leading-snug">{hint}</span>
      </span>
    </div>
  );
}
